/** Use Kaching's displayed deal total; Shopify still owns cart pricing. */
function getMemorialBundlePrice(productInfo, fallbackPrice) {
  if (productInfo.dataset.productId !== '12679566754110') return fallbackPrice;
  const bundleX = productInfo.querySelector('.bx-widget-container');
  if (bundleX) {
    const additions = [...bundleX.querySelectorAll('.bx-addon-row')].filter(row =>
      row.querySelector('input.js-bx-addon-checkbox')?.checked
    );
    if (!additions.length) return fallbackPrice;

    try {
      const currency = productInfo.querySelector('variant-selects')?.dataset.currencyCode
        || fallbackPrice?.match(/\b[A-Z]{3}\b/u)?.[0];
      if (!currency) return fallbackPrice;
      const locale = productInfo.ownerDocument?.documentElement.lang || 'en';
      const formatter = new Intl.NumberFormat(locale, { style: 'currency', currency });
      const parts = formatter.formatToParts(12345.6);
      const group = parts.find(part => part.type === 'group')?.value;
      const decimal = parts.find(part => part.type === 'decimal')?.value;
      const scale = 10 ** formatter.resolvedOptions().maximumFractionDigits;
      const amounts = [fallbackPrice, ...additions.map(row =>
        row.querySelector('.bx-addon-prices .bx-discounted-price')?.textContent.trim()
      )];
      let total = 0;
      for (const amount of amounts) {
        if (!amount) return fallbackPrice;
        let normalized = group ? amount.replaceAll(group, '') : amount;
        if (decimal) normalized = normalized.replace(decimal, '.');
        normalized = normalized.replace(/[^\d.-]/g, '');
        if (!normalized || !Number.isFinite(Number(normalized))) return fallbackPrice;
        total += Math.round(Number(normalized) * scale);
      }
      const formatted = formatter.format(total / scale);
      return formatted.includes(currency) ? formatted : `${formatted} ${currency}`;
    } catch {
      return fallbackPrice;
    }
  }
  const bundle = productInfo.querySelector('kaching-bundles-block');
  const selectedPrice = bundle?.querySelector('.kaching-bundles__bar--selected .kaching-bundles__bar-price');
  const displayedPrice = selectedPrice?.textContent.trim();
  if (!displayedPrice) return fallbackPrice;

  try {
    const config = JSON.parse(bundle.getAttribute('config') || '{}');
    const deal = JSON.parse(bundle.getAttribute('deal-block') || '{}');
    const currency = config.marketCurrencyCode || fallbackPrice?.match(/\b[A-Z]{3}\b/u)?.[0];
    const additions = deal.includeUpsellsInDealBarPrice ? [] : [...bundle.querySelectorAll(
      '.kaching-bundles__checkbox-upsells__upsell [role="checkbox"][aria-checked="true"]'
    )];
    if (!additions.length) {
      return currency && !displayedPrice.includes(currency) ? `${displayedPrice} ${currency}` : displayedPrice;
    }
    if (!currency) return fallbackPrice;

    const formatter = new Intl.NumberFormat(config.liquidLocale || config.locale || 'en', { style: 'currency', currency });
    const parts = formatter.formatToParts(12345.6);
    const group = parts.find(part => part.type === 'group')?.value;
    const decimal = parts.find(part => part.type === 'decimal')?.value;
    const minorDigits = formatter.resolvedOptions().maximumFractionDigits;
    const scale = 10 ** minorDigits;
    const amounts = [displayedPrice, ...additions.map(addition => addition.querySelector('.kaching-bundles__bar-price')?.textContent.trim())];
    let total = 0;
    for (const amount of amounts) {
      if (!amount) return fallbackPrice;
      let normalized = group ? amount.replaceAll(group, '') : amount;
      if (decimal) normalized = normalized.replace(decimal, '.');
      normalized = normalized.replace(/[^\d.-]/g, '');
      if (!normalized || !Number.isFinite(Number(normalized))) return fallbackPrice;
      total += Math.round(Number(normalized) * scale);
    }
    const formatted = formatter.format(total / scale);
    return formatted.includes(currency) ? formatted : `${formatted} ${currency}`;
  } catch {
    return fallbackPrice;
  }
}

function prepareHeartRoundQuoteThumbnails(root) {
  root.querySelectorAll('[data-bonaper-field="quote"] img.bonaper-swatch__img').forEach(image => {
    if (image.dataset.heartRoundQuoteReady) return;
    image.dataset.heartRoundQuoteReady = 'true';
    // These are display-only thumbnails, not images read by the compositor.
    // Safari should not wait for lazy image layout or a canvas CORS request.
    image.removeAttribute('crossorigin');
    image.loading = 'eager';
    image.decoding = 'async';
    image.width = 48;
    image.height = 48;
    const retry = () => {
      if (image.dataset.heartRoundQuoteRetried) return;
      image.dataset.heartRoundQuoteRetried = 'true';
      const source = new URL(image.src, document.baseURI);
      source.searchParams.set('quote_thumbnail_retry', '1');
      image.src = source.href;
    };
    image.addEventListener('error', retry, { once: true });
    if (image.complete && !image.naturalWidth) retry();
  });
}

class HeartRoundPhotoNotes {
  constructor(root) {
    this.root = root;
    this.controller = new AbortController();
    const signal = this.controller.signal;
    root.addEventListener('change', event => {
      if (event.target.matches('[data-bonaper-field="photo"] input[type="file"]') && event.target.files?.length) this.pendingEditor = true;
    }, { capture: true, signal });
    root.addEventListener('click', event => {
      if (event.target.closest('[data-bonaper-field="photo"] .bonaper-photo__edit')) this.pendingEditor = true;
      if (event.target.closest('[data-bonaper-field="photo"] .bonaper-photo__guide')) {
        this.pendingGuide = true;
        queueMicrotask(() => this.enhanceGuide());
      }
    }, { capture: true, signal });
    this.observer = new MutationObserver(() => this.sync());
    this.observer.observe(document.body, { childList: true, subtree: true });
    this.sync();
  }

  sync() {
    this.enhanceGuide();
    const nativeField = this.root.querySelector('[data-bonaper-field="photo_preview_notes"]');
    if (nativeField && nativeField !== this.field) {
      this.field = nativeField;
      this.parkHost = nativeField.parentElement;
      this.field.classList.add('heart-round-photo-notes-field');
      const input = this.field.querySelector('textarea');
      if (input) {
        input.rows = 2;
        // BonaPer's editor focus trap includes explicit tabindex controls.
        input.tabIndex = 0;
        input.addEventListener('keydown', event => {
          // Typing and cursor movement must not also move/rotate the photo.
          if (event.key !== 'Tab' && event.key !== 'Escape') event.stopPropagation();
        }, { signal: this.controller.signal });
      }
    }
    if (!this.field) return;
    if (this.editor && !this.editor.isConnected) this.restore();
    const editor = document.querySelector('.bonaper-pedit-host .bonaper-pedit');
    if (editor && (this.pendingEditor || editor === this.editor)) {
      if (editor === this.editor) return;
      const footer = editor.querySelector('.bonaper-pedit__footer');
      if (!footer) return;
      this.editor = editor;
      editor.classList.add('heart-round-photo-notes-editor');
      this.pendingEditor = false;
      this.details = document.createElement('details');
      this.details.className = 'heart-round-photo-notes';
      const summary = document.createElement('summary');
      summary.textContent = 'Photo preview not quite right? Don’t worry — leave a note and our design team will adjust it for you.';
      this.details.append(summary, this.field);
      footer.classList.add('heart-round-photo-notes-footer');
      footer.prepend(this.details);
    } else if (!this.editor) {
      this.parkField();
    }
  }

  parkField() {
    const host = this.parkHost?.isConnected ? this.parkHost : this.root.querySelector('[data-bonaper-line-fields]');
    if (host && this.field.parentElement !== host) host.prepend(this.field);
  }

  enhanceGuide() {
    if (!this.pendingGuide) return;
    const guide = document.querySelector('.bonaper-guide');
    const steps = guide?.querySelector('.bonaper-guide__steps');
    if (!steps) return;
    this.pendingGuide = false;
    if (guide.dataset.heartRoundPhotoGuide) return;
    guide.dataset.heartRoundPhotoGuide = 'true';
    guide.classList.add('heart-round-photo-guide');
    const close = guide.querySelector('.bonaper-modal__close');
    const heading = guide.querySelector('.bonaper-guide__title');
    if (close && heading) {
      const header = document.createElement('div');
      header.className = 'heart-round-photo-guide__header';
      close.classList.add('heart-round-photo-guide__close');
      close.textContent = 'Close ×';
      header.append(heading, close);
      guide.insertBefore(header, steps);
      const done = document.createElement('button');
      done.type = 'button';
      done.className = 'heart-round-photo-guide__done';
      done.textContent = 'Close Photo Guide';
      // Reuse BonaPer's native close handler and focus restoration.
      done.addEventListener('click', () => close.click());
      guide.append(done);
    }
    const tips = document.createElement('li');
    tips.className = 'bonaper-guide__step heart-round-photo-guide__tips';
    const title = document.createElement('h3');
    title.textContent = 'Choose a photo for the best result';
    const list = document.createElement('ul');
    for (const text of [
      'Choose a clear, close-up photo taken at your pet’s eye level. Keep the face and both ears fully visible.',
      'Use bright, even natural light. Avoid blurry, dark or heavily filtered photos and small screenshots.',
      'Choose one pet per photo and leave a little space around the head so you can adjust its position in the editor.',
    ]) {
      const item = document.createElement('li');
      item.textContent = text;
      list.append(item);
    }
    tips.append(title, list);
    steps.prepend(tips);
    const note = document.createElement('li');
    note.className = 'bonaper-guide__step heart-round-photo-guide__note';
    const text = document.createElement('p');
    text.className = 'bonaper-guide__step-text';
    text.textContent = 'Photo preview not quite right? Don’t worry — while editing your uploaded photo, open the note section and tell our design team what to adjust. Your note will be saved with your order and will not be engraved.';
    note.append(text);
    const failedUpload = steps.lastElementChild;
    failedUpload.before(note);
    const isMemorialRefreshedProduct = this.root.dataset.productId === '12679566754110';
    const imageUrl = isMemorialRefreshedProduct
      ? 'https://limency.com/cdn/shop/files/il_fullxfull.6859518457_64lp.jpg'
      : this.root.closest('memorial-product-page')?.querySelector('.product__media img[src*="limency-memorial-before-after"]')?.getAttribute('src');
    if (imageUrl) {
      const example = document.createElement('li');
      example.className = 'bonaper-guide__step heart-round-photo-guide__example';
      const caption = document.createElement('p');
      caption.className = 'bonaper-guide__step-text';
      caption.textContent = 'Example: a clear pet photo and the engraved result.';
      const image = document.createElement('img');
      image.src = imageUrl;
      image.alt = isMemorialRefreshedProduct
        ? 'Examples of suitable and unsuitable photos for submission.'
        : 'A clear, close-up pet photo beside the engraved memorial wind chime.';
      image.width = isMemorialRefreshedProduct ? 3000 : 1022;
      image.height = isMemorialRefreshedProduct ? 3000 : 1280;
      image.loading = 'eager';
      image.decoding = 'async';
      example.append(caption, image);
      steps.append(example);
    }
  }

  restore() {
    // Move the real BonaPer field back, retaining its native input handlers,
    // validation, draft state and cart-property serialization. No duplicate field.
    this.parkField();
    this.editor?.classList.remove('heart-round-photo-notes-editor');
    this.details?.remove();
    this.details = null;
    this.editor = null;
  }

  disconnect() {
    this.observer.disconnect();
    this.controller.abort();
    this.restore();
  }
}

if (!customElements.get('memorial-reviews')) {
  customElements.define('memorial-reviews', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      this.cards = this.querySelector('[data-review-cards]');
      this.status = this.querySelector('[role="status"]');
      this.more = this.querySelector('button');
      this.more.addEventListener('click', () => this.load(this.nextPage), { signal: this.controller.signal });
      this.load(1);
    }

    disconnectedCallback() {
      this.controller.abort();
    }

    async load(page) {
      if (this.loading || !Number.isSafeInteger(page) || page < 1) return;
      this.loading = true;
      this.more.disabled = true;
      this.setAttribute('aria-busy', 'true');
      this.status.textContent = 'Loading customer reviews…';
      try {
        const response = await fetch(`/apps/bonatrust/widget/${encodeURIComponent(this.dataset.handle)}?page=${page}&sort=recent`, { signal: this.controller.signal });
        if (!response.ok) throw new Error('Reviews unavailable');
        const fragment = new DOMParser().parseFromString(await response.text(), 'text/html');
        const root = fragment.querySelector('.bt-widget');
        if (root?.querySelector('.bt-title')?.textContent.trim() === 'Reviews for this store') {
          this.status.textContent = 'No reviews for this product yet.';
          this.more.hidden = true;
          return;
        }
        if (!root || root.querySelector('.bt-title')?.textContent.trim() !== 'Reviews for this item') throw new Error('Product reviews unavailable');
        // Preserve BonaTrust's published text and ratings; never inject its HTML or scripts.
        const reviews = [...root.querySelectorAll('.bt-card')].filter(card => !card.querySelector('.bt-badge-store'));
        const added = document.createDocumentFragment();
        for (const review of reviews) {
          const card = document.createElement('article');
          card.className = 'memorial-reviews__card';
          const stars = document.createElement('div');
          const rating = Math.min(5, Math.max(0, Number(review.querySelector('.bt-num')?.textContent) || 0));
          stars.className = 'memorial-reviews__stars';
          stars.setAttribute('role', 'img');
          stars.setAttribute('aria-label', `${rating} out of 5 stars`);
          stars.textContent = '★'.repeat(rating) + '☆'.repeat(5 - rating);
          card.append(stars);
          const title = review.querySelector('.bt-card-title')?.textContent.trim();
          if (title) {
            const heading = document.createElement('h3');
            heading.className = 'memorial-reviews__title';
            heading.textContent = title;
            card.append(heading);
          }
          const body = document.createElement('p');
          body.className = 'memorial-reviews__body';
          body.textContent = review.querySelector('.bt-card-body')?.textContent.trim() || '';
          const author = document.createElement('p');
          author.className = 'memorial-reviews__author';
          author.textContent = review.querySelector('.bt-head-right b')?.textContent.trim() || 'Customer';
          const date = document.createElement('time');
          date.className = 'memorial-reviews__date';
          date.textContent = review.querySelector('time')?.textContent.trim() || '';
          card.append(body, author, date);
          added.append(card);
        }
        this.cards.append(added);
        this.nextPage = Number(root.querySelector('[data-bt-page]')?.dataset.btPage) || null;
        this.more.hidden = !this.nextPage;
        const average = root.querySelector('.bt-avg')?.textContent.trim();
        const count = root.querySelector('.bt-avg-meta')?.textContent.match(/\((\d+) reviews\)/)?.[1];
        this.status.textContent = reviews.length ? `${average} out of 5 · ${count || this.cards.children.length} reviews · BonaTrust` : 'No customer reviews yet.';
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.status.textContent = 'Customer reviews could not be loaded. Please try again.';
        this.nextPage = page;
        this.more.hidden = false;
      } finally {
        this.loading = false;
        this.more.disabled = false;
        this.setAttribute('aria-busy', 'false');
      }
    }
  });
}

/* The dock delegates to Dawn/BonaPer's original submit button. */
if (!customElements.get('memorial-product-page')) {
  customElements.define('memorial-product-page', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      this.productInfo = this.querySelector('product-info');
      this.dock = this.querySelector('[data-memorial-dock]');
      this.dockButton = this.querySelector('[data-memorial-add]');
      const data = this.querySelector('[data-memorial-variants]');
      if (!this.productInfo || !this.dock || !this.dockButton || !data) return;
      this.photoNotes = new HeartRoundPhotoNotes(this.productInfo);
      this.variants = JSON.parse(data.textContent).variants;
      this.dockButton.addEventListener('click', () => {
        const submit = this.productInfo.querySelector('.product-form__submit');
        if (!submit || submit.disabled || submit.getAttribute('aria-disabled') === 'true') return;
        // .click() preserves BonaPer's validation, customization save and properties.
        submit.click();
        const error = this.productInfo.querySelector('[data-bonaper-field-error]:not([hidden])');
        if (error) error.scrollIntoView({ block: 'center', behavior: 'auto' });
      }, { signal: this.controller.signal });
      this.productInfo.addEventListener('change', () => this.sync(), { signal: this.controller.signal });
      this.productInfo.addEventListener('input', () => this.sync(), { signal: this.controller.signal });
      this.productInfo.addEventListener('submit', event => {
        if (!event.target.matches('form[data-type="add-to-cart-form"]')) return;
        this.bindPersonalizationGuard();
        if (this.guardedForm === event.target) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.productInfo.querySelector('product-form')?.handleErrorMessage(this.dataset.saveError);
      }, { capture: true, signal: this.controller.signal });
      this.observer = new MutationObserver(() => this.sync());
      this.observer.observe(this.productInfo, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'value', 'aria-busy', 'class', 'aria-checked'] });
      this.intersection = new IntersectionObserver(() => this.updateDockVisibility());
      window.addEventListener('scroll', () => {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = null;
          this.updateDockVisibility();
        });
      }, { passive: true, signal: this.controller.signal });
      const button = this.productInfo.querySelector('.product-form__submit');
      if (button) this.intersection.observe(button);
      this.sync();
    }

    sync() {
      prepareHeartRoundQuoteThumbnails(this.productInfo);
      this.bindPersonalizationGuard();
      this.updateDockVisibility();
      const submit = this.productInfo.querySelector('.product-form__submit');
      const id = this.productInfo.querySelector('form[data-type="add-to-cart-form"] input[name="id"]')?.value;
      const variant = this.variants.find(item => String(item.id) === id);
      const price = getMemorialBundlePrice(this.productInfo, variant?.price);
      this.dockButton.disabled = !submit || submit.disabled || submit.getAttribute('aria-disabled') === 'true';
      const submitLabel = submit?.querySelector('span');
      const addLabel = this.dataset.addLabel;
      // Dawn replaces this label after a variant change. Reapply the actual
      // selected price while preserving sold-out, unavailable and busy states.
      if (variant && submitLabel && !submit.disabled && addLabel && submitLabel.textContent.trim().startsWith(addLabel)) {
        const pricedLabel = `${addLabel} — ${price}`;
        if (submitLabel.textContent !== pricedLabel) submitLabel.textContent = pricedLabel;
      }
      const label = submitLabel?.textContent.trim();
      if (label && this.dockButton.textContent !== label) this.dockButton.textContent = label;
      if (!variant) return;
      const dockPrice = this.querySelector('[data-memorial-price]');
      if (dockPrice.textContent !== price) dockPrice.textContent = price;
      this.querySelector('[data-memorial-variant]').textContent = variant.title;
    }

    // Keep the dock available whenever the real Add to cart button is off
    // screen (above or below) once the shopper has passed the size picker, so
    // it stays usable while personalizing and while choosing upsells.
    updateDockVisibility() {
      const submit = this.productInfo.querySelector('.product-form__submit');
      if (!submit) {
        this.dock.hidden = true;
        return;
      }
      const viewport = window.innerHeight || document.documentElement.clientHeight;
      const rect = submit.getBoundingClientRect();
      const submitOnScreen = rect.height > 0 && rect.bottom > 0 && rect.top < viewport;
      const start = this.productInfo.querySelector('variant-selects') || this.productInfo.querySelector('.product__title');
      const pastStart = start ? start.getBoundingClientRect().bottom < 0 : rect.bottom < 0;
      this.dock.hidden = submitOnScreen || !pastStart;
    }

    bindPersonalizationGuard() {
      const fields = this.productInfo.querySelector('[data-bonaper-fields]');
      const form = this.productInfo.querySelector('form[data-type="add-to-cart-form"]');
      if (!form || this.guardedForm === form || fields?.getAttribute('aria-busy') !== 'false') return;
      this.guardedForm = form;
      // Bind after BonaPer has initialized its form capture handler. Its first
      // event is canceled while saving; a successful replay carries both keys.
      form.addEventListener('submit', event => {
        if (event.defaultPrevented) return;
        const id = form.querySelector('[name="properties[_bonaper_id]"]')?.value;
        const version = form.querySelector('[name="properties[_bonaper_version]"]')?.value;
        if (id && /^[1-9]\d*$/.test(version || '')) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        const error = this.productInfo.querySelector('product-form');
        error?.handleErrorMessage(this.dataset.saveError);
      }, { capture: true, signal: this.controller.signal });
    }

    disconnectedCallback() {
      this.photoNotes?.disconnect();
      this.controller?.abort();
      this.observer?.disconnect();
      this.intersection?.disconnect();
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
    }
  });
}

if (!customElements.get('memorial-finalize')) {
  customElements.define('memorial-finalize', class extends HTMLElement {
    connectedCallback() {
      this.main = document.querySelector('memorial-product-page');
      this.info = this.main?.querySelector('product-info');
      if (!this.info) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.status = this.querySelector('[data-finalize-status]');
      this.cartLink = this.querySelector('[data-finalize-cart]');
      this.addEventListener('input', event => {
        const token = event.target.dataset.finalizeField;
        if (!token) return;
        const original = this.info.querySelector(`input[id$="-${token}"]`);
        if (!original) return;
        original.value = event.target.value;
        original.dispatchEvent(new Event('input', { bubbles: true }));
        original.dispatchEvent(new Event('change', { bubbles: true }));
        if (token === 'petName' && event.target.value.trim()) this.showNameError(false);
        this.status.textContent = '';
        this.cartLink.hidden = true;
      }, options);
      this.addEventListener('click', event => {
        const size = event.target.closest('[data-finalize-variant]');
        if (size) {
          if (size.getAttribute('aria-pressed') === 'true' || this.pendingVariant || this.pendingAdd) return;
          const variants = JSON.parse(this.main.querySelector('[data-memorial-variants]').textContent).variants;
          const index = variants.findIndex(v => String(v.id) === size.dataset.finalizeVariant);
          const variant = variants[index];
          const picker = this.info.querySelector('variant-selects');
          let original;
          if (variant?.options) {
            const groups = [...picker.querySelectorAll('fieldset')];
            const matching = variant.options.map((value, optionIndex) => [...(groups[optionIndex]?.querySelectorAll('input[type="radio"]') || [])].find(input => input.value === value));
            if (matching.some(input => !input)) return;
            // Update every native option before one change event, avoiding
            // racing requests when size and color change together.
            for (const input of matching) {
              if (!input.checked) original = input;
              input.checked = true;
            }
          } else {
            original = picker.querySelectorAll('input[type="radio"]')[index];
          }
          if (!original) return;
          this.pendingVariant = size.dataset.finalizeVariant;
          this.variantViewport = window.scrollY;
          this.status.textContent = 'Updating your selection…';
          this.cartLink.hidden = true;
          if (variant?.options) original.dispatchEvent(new Event('change', { bubbles: true }));
          else original.click();
          this.sync();
        }
        if (event.target.closest('[data-finalize-photo]')) {
          // Open the existing BonaPer chooser in this user gesture. Its own
          // change handler still performs upload, cropping and preview work.
          this.info.querySelector('[data-bonaper-field="photo"] input[type="file"]')?.click();
        }
        if (event.target.closest('[data-finalize-add]')) {
          const submit = this.info.querySelector('.product-form__submit');
          if (this.pendingAdd || this.pendingVariant || !submit || submit.disabled || submit.getAttribute('aria-disabled') === 'true') return;
          const name = this.querySelector('[data-finalize-field="petName"]');
          if (!name.value.trim()) {
            this.showNameError(true);
            name.focus({ preventScroll: true });
            return;
          }
          this.pendingAdd = this.info.querySelector('form[data-type="add-to-cart-form"] [name="id"]')?.value;
          this.status.textContent = 'Saving your personalization…';
          this.cartLink.hidden = true;
          this.querySelector('[data-finalize-add]').disabled = true;
          submit.click();
          this.sync();
        }
      }, options);
      this.main.addEventListener('input', () => this.sync(), options);
      this.main.addEventListener('change', () => this.sync(), options);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeCart = subscribe(PUB_SUB_EVENTS.cartUpdate, event => {
          if (!this.pendingAdd || String(event.productVariantId) !== this.pendingAdd || event.source !== 'product-form') return;
          this.pendingAdd = null;
          this.status.textContent = 'Added to your cart.';
          this.cartLink.hidden = false;
          this.sync();
        });
        this.unsubscribeError = subscribe(PUB_SUB_EVENTS.cartError, event => {
          if (!this.pendingAdd || String(event.productVariantId) !== this.pendingAdd) return;
          this.pendingAdd = null;
          this.status.textContent = typeof event.errors === 'string' ? event.errors : 'Unable to add this item. Please try again.';
          this.sync();
        });
      }
      this.observer = new MutationObserver(() => this.sync());
      this.observer.observe(this.main, { subtree: true, childList: true, attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'aria-busy', 'value', 'hidden'] });
      this.sync();
    }

    showNameError(show) {
      this.querySelector('[data-finalize-name-error]').hidden = !show;
      this.querySelector('[data-finalize-field="petName"]').setAttribute('aria-invalid', String(show));
    }

    sync() {
      this.info = this.main.querySelector('product-info');
      if (!this.info) return;
      const id = this.info.querySelector('form[data-type="add-to-cart-form"] [name="id"]')?.value;
      const submit = this.info.querySelector('.product-form__submit');
      if (this.pendingVariant && this.pendingVariant === id && submit && !submit.disabled) {
        const selected = this.querySelector(`[data-finalize-variant="${id}"]`);
        this.pendingVariant = null;
        this.status.textContent = '';
        // Dawn focuses the native option after its response. Keep users at
        // the closing card when that request originated from this card.
        selected?.focus({ preventScroll: true });
        window.scrollTo({ top: this.variantViewport, behavior: 'instant' });
      }
      this.querySelectorAll('[data-finalize-variant]').forEach(button => {
        button.setAttribute('aria-pressed', String(button.dataset.finalizeVariant === id));
      });
      for (const mirror of this.querySelectorAll('[data-finalize-field]')) {
        const original = this.info.querySelector(`input[id$="-${mirror.dataset.finalizeField}"]`);
        mirror.disabled = !original;
        if (original && document.activeElement !== mirror) mirror.value = original.value;
      }
      const ready = this.info.querySelector('[data-bonaper-fields]')?.getAttribute('aria-busy') === 'false' && !!this.info.querySelector('input[id$="-petName"]');
      const issue = this.info.querySelector('[data-bonaper-field-error]:not([hidden]), .product-form__error-message-wrapper:not([hidden])');
      if (this.pendingAdd && issue?.textContent.trim()) {
        this.pendingAdd = null;
        this.status.textContent = issue.textContent.trim();
      }
      this.querySelector('[data-finalize-add]').disabled = !ready || !!this.pendingAdd || !!this.pendingVariant || !submit || submit.disabled || submit.getAttribute('aria-disabled') === 'true';
      this.querySelector('[data-finalize-photo]').disabled = !ready || !!this.pendingAdd;
      this.setAttribute('aria-busy', String(!!this.pendingAdd || !!this.pendingVariant));
      const price = this.main.querySelector('[data-memorial-price]')?.textContent;
      const priceLabel = this.querySelector('[data-finalize-price]');
      if (price && priceLabel.textContent !== price) priceLabel.textContent = price;
      const fileName = this.info.querySelector('[data-bonaper-field="photo"] input[type="file"]')?.files?.[0]?.name;
      this.querySelector('[data-finalize-photo]').textContent = fileName ? `Change photo — ${fileName}` : 'Upload Your Favorite Photo';
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.unsubscribeCart?.();
      this.unsubscribeError?.();
    }
  });
}
if (!customElements.get('heart-round-gallery')) {
  customElements.define('heart-round-gallery', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.gallery = this.closest('media-gallery');
      this.list = this.querySelector('.product__media-list');
      if (!this.gallery || !this.list) return;
      this.addEventListener('click', event => {
        const arrow = event.target.closest('[data-gallery-step]');
        if (arrow) this.move(Number(arrow.dataset.galleryStep));
      }, options);
      this.addEventListener('keydown', event => {
        if (event.target.closest('video, iframe, input, select, textarea')) return;
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        this.move(event.key === 'ArrowRight' ? 1 : -1);
      }, options);
      this.list.addEventListener('touchstart', event => {
        this.touch = event.touches.length === 1 && !event.target.closest('video, iframe') ? { x: event.touches[0].clientX, y: event.touches[0].clientY } : null;
      }, { ...options, passive: true });
      this.list.addEventListener('touchend', event => {
        if (!this.touch || !event.changedTouches.length) return;
        const dx = event.changedTouches[0].clientX - this.touch.x;
        const dy = event.changedTouches[0].clientY - this.touch.y;
        this.touch = null;
        if (Math.abs(dx) > 48 && Math.abs(dx) > Math.abs(dy) * 1.5) this.move(dx < 0 ? 1 : -1);
      }, { ...options, passive: true });
      this.observer = new MutationObserver(() => this.updateCount());
      this.observer.observe(this.list, { subtree: true, attributes: true, attributeFilter: ['class'] });
      this.updateCount();
    }

    move(step) {
      const slides = [...this.list.querySelectorAll('.product__media-item[data-media-id]')];
      if (slides.length < 2 || typeof this.gallery.setActiveMedia !== 'function') return;
      const index = Math.max(0, slides.findIndex(slide => slide.classList.contains('is-active')));
      const next = slides[(index + step + slides.length) % slides.length];
      this.gallery.setActiveMedia(next.dataset.mediaId, false);
      if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        next.animate([{ opacity: 0.45, transform: `translateX(${step * 14}px)` }, { opacity: 1, transform: 'translateX(0)' }], { duration: 180, easing: 'ease-out' });
      }
      this.updateCount();
    }

    updateCount() {
      const slides = [...this.list.querySelectorAll('.product__media-item[data-media-id]')];
      const count = this.querySelector('[data-gallery-count]');
      const label = `${Math.max(0, slides.findIndex(slide => slide.classList.contains('is-active'))) + 1} / ${slides.length}`;
      if (count && count.textContent !== label) count.textContent = label;
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
    }
  });
}
